import { FC, useCallback, useMemo, useRef, useState } from 'react';

import { DateRange } from 'react-day-picker';

import { Button } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getSelectProjectOptions } from 'utils/projectsUtil';

import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode, DatePickerValue } from 'components/DatePicker/DatePicker.types';
import { Modal } from 'components/Modal/Modal';

import { ProjectSelect } from '../ProjectSelect';

interface GenerateReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGenerate: (startDate: string, endDate: string, projectIds: string[]) => void;
}

const GenerateReportModal: FC<GenerateReportModalProps> = ({
    isOpen,
    onClose,
    onGenerate
}) => {
    const [dateRange, setDateRange] = useState<DatePickerValue>();
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

    const { userDetails } = useUserDetails();
    const projectsDetails = userDetails?.projects;

    const { getPermissions } = usePermissions();
    const permissions = getPermissions();
    const allowedPublishers =
        permissionsUtil.getPublishersByFinancialReports(permissions);

    const projectOptions = useMemo(
        () =>
            projectsDetails
                ? getSelectProjectOptions(projectsDetails, allowedPublishers).map(
                    (project) => ({
                        value: project.value,
                        label: project.content as string
                    })
                )
                : [],
        [projectsDetails, allowedPublishers]
    );

    const selectedProjectsRef = useRef(selectedProjects);
    selectedProjectsRef.current = selectedProjects;

    const handleProjectChange = useCallback((value: string) => {
        const currentProjects = selectedProjectsRef.current;
        const newSelectedProjects = currentProjects.includes(value)
            ? currentProjects.filter((p) => p !== value)
            : [...currentProjects, value];
        setSelectedProjects(newSelectedProjects);
    }, []);

    const handleBulkProjectChange = useCallback((values: string[]) => {
        setSelectedProjects(values);
    }, []);

    const handleDateApply = useCallback((value: DatePickerValue) => {
        setDateRange(value);
    }, []);

    const handleDateClear = useCallback(() => {
        setDateRange(undefined);
    }, []);

    const handleGenerate = () => {
        if (dateRange && selectedProjects.length > 0) {
            const isDateRange = (value: DatePickerValue): value is DateRange => {
                return (
                    typeof value === 'object' &&
                    value !== null &&
                    'from' in value &&
                    'to' in value
                );
            };

            if (isDateRange(dateRange) && dateRange.from && dateRange.to) {
                // Format date as YYYY-MM-DD in LOCAL timezone
                const formatLocalDate = (date: Date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                const startDate = formatLocalDate(dateRange.from);
                const endDate = formatLocalDate(dateRange.to);

                onGenerate(startDate, endDate, selectedProjects);
                // Reset form
                setDateRange(undefined);
                setSelectedProjects([]);
                onClose();
            }
        }
    };

    const handleCancel = () => {
        // Reset form
        setDateRange(undefined);
        setSelectedProjects([]);
        onClose();
    };

    const isGenerateDisabled = !dateRange || selectedProjects.length === 0;

    return (
        <Modal isOpen={isOpen} onClose={handleCancel} width={440}>
            <div className="w-full flex flex-col gap-14">
                <div className="flex flex-col gap-1">
                    <div className="font-semibold text-base-primary">Generate new report</div>
                </div>

                <div className="flex flex-col gap-6 w-full px-6">
                    <div className="w-full">
                        <DatePicker
                            label="Pick a date"
                            mode={DatePickerMode.RANGE}
                            value={dateRange}
                            onApply={handleDateApply}
                            onClear={handleDateClear}
                            placeholder="Pick a date"
                            numberOfMonths={1}
                            shouldDisplayTime={false}
                            shouldExcludeFuture={true}
                            dataTestId="GenerateReportModal-DatePicker"
                            className="w-full"
                            labelSpacing="mb-2"
                        />
                    </div>

                    <div className="w-full">
                        {/*  This is TODO: Task ACDEV-18482 - will be changed with reusable component from shared-ui */}
                        <ProjectSelect
                            label="Project Name"
                            items={projectOptions}
                            selectedItems={selectedProjects}
                            onChange={handleProjectChange}
                            onBulkChange={handleBulkProjectChange}
                            placeholder="Select projects"
                            searchPlaceholder="Search..."
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 w-full">
                    <Button
                        variant="link"
                        onClick={handleCancel}
                        className="text-base-primary hover:text-base-primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerate}
                        disabled={isGenerateDisabled}
                        className="bg-gray-900 hover:bg-gray-800"
                    >
                        Generate report
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GenerateReportModal;

